import { useQuery } from "@tanstack/react-query";
import { analyzeGraph, type GraphEdge, type GraphState } from "@facade/dashboard-shared";
import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Bars } from "../components/charts";
import { api } from "../lib/api";
import { fmtAgo, shortAct, shortFp, tail } from "../lib/format";
import { useLive } from "../lib/ws";

cytoscape.use(dagre);

type Sel = { type: "state"; fp: string } | { type: "edge"; key: string } | null;

export function GraphView() {
  const { deviceId = "" } = useParams();
  const q = useQuery({ queryKey: ["graph", deviceId], queryFn: () => api.graph(deviceId) });
  const [liveOn, setLiveOn] = useState(true);
  const live = useLive({ deviceId, kinds: ["graph.*"] }, { limit: 500, enabled: liveOn });
  const [pkgFilter, setPkgFilter] = useState<string>("");
  const [sel, setSel] = useState<Sel>(null);
  const ref = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);

  // Snapshot + live deltas → current graph.
  const graph = useMemo(() => {
    const states = new Map<string, GraphState>();
    const edges = new Map<string, GraphEdge>();
    for (const s of q.data?.states ?? []) states.set(s.fp, s);
    for (const e of q.data?.edges ?? []) edges.set(`${e.fromFp}|${e.action}|${e.toFp}`, e);
    const snapshotTs = q.dataUpdatedAt;
    for (const ev of live) {
      if (ev.receivedTs && ev.receivedTs < snapshotTs) continue;
      const p = ev.payload as any;
      if (ev.kind === "graph.state" && p?.fp) {
        const prev = states.get(p.fp);
        states.set(p.fp, { ...prev, ...p, visits: p.visits ?? (prev?.visits ?? 0) + 1, sampleIds: p.sampleIds ?? prev?.sampleIds ?? [] });
      } else if (ev.kind === "graph.edge" && p?.fromFp) {
        const k = `${p.fromFp}|${p.action}|${p.toFp}`;
        const prev = edges.get(k);
        edges.set(k, { ...prev, ...p, count: p.count ?? (prev?.count ?? 0) + 1, okCount: p.okCount ?? (prev?.okCount ?? 0) + (p.ok === false ? 0 : 1), removed: false });
      } else if (ev.kind === "graph.edge.remove" && p?.fromFp) {
        const k = `${p.fromFp}|${p.action}|${p.toFp}`;
        const prev = edges.get(k);
        if (prev) edges.set(k, { ...prev, removed: true });
      }
    }
    return { states: [...states.values()], edges: [...edges.values()].filter((e) => !e.removed) };
  }, [q.data, q.dataUpdatedAt, live]);

  const analysis = useMemo(() => analyzeGraph(graph), [graph]);
  const pkgs = useMemo(() => [...new Set(graph.states.map((s) => s.pkg ?? ""))].sort(), [graph]);
  const byFp = useMemo(() => new Map(graph.states.map((s) => [s.fp, s])), [graph]);

  useEffect(() => {
    if (!ref.current) return;
    const states = graph.states.filter((s) => !pkgFilter || s.pkg === pkgFilter);
    const fps = new Set(states.map((s) => s.fp));
    const edges = graph.edges.filter((e) => fps.has(e.fromFp) && fps.has(e.toFp));
    const maxVisits = Math.max(1, ...states.map((s) => s.visits ?? 1));
    const elements: cytoscape.ElementDefinition[] = [
      ...states.map((s) => ({
        data: { id: s.fp, label: `${shortAct(s.activity)}\n${shortFp(s.fp)}`, size: 24 + 36 * Math.sqrt((s.visits ?? 1) / maxVisits) },
      })),
      ...edges.map((e) => ({
        data: {
          id: `${e.fromFp}|${e.action}|${e.toFp}`,
          source: e.fromFp,
          target: e.toFp,
          label: e.action,
          width: 1 + Math.min(6, Math.log2((e.count ?? 1) + 1)),
          fail: e.count ? 1 - (e.okCount ?? e.count) / e.count : 0,
          commit: e.action.includes("!") ? 1 : 0,
        },
      })),
    ];
    const css = getComputedStyle(document.documentElement);
    const c = (v: string) => css.getPropertyValue(v).trim();
    if (!cyRef.current) {
      cyRef.current = cytoscape({
        container: ref.current,
        elements,
        style: [
          { selector: "node", style: { label: "data(label)", "text-wrap": "wrap", "font-size": 9, "text-valign": "bottom", "text-margin-y": 4, color: c("--text-2"), "background-color": c("--series-1"), width: "data(size)", height: "data(size)", "border-width": 2, "border-color": c("--bg") } },
          { selector: "node:selected", style: { "border-color": c("--text"), "border-width": 3 } },
          { selector: "edge", style: { "curve-style": "bezier", "target-arrow-shape": "triangle", width: "data(width)", "line-color": c("--border"), "target-arrow-color": c("--border"), label: "data(label)", "font-size": 7, color: c("--muted"), "text-rotation": "autorotate", "text-background-color": c("--panel"), "text-background-opacity": 0.8, "text-background-padding": "1px" } },
          { selector: "edge[fail > 0.34]", style: { "line-color": c("--bad"), "target-arrow-color": c("--bad") } },
          { selector: "edge[commit = 1]", style: { "line-style": "dashed", "line-color": c("--ns-graph"), "target-arrow-color": c("--ns-graph") } },
          { selector: "edge:selected", style: { "line-color": c("--text"), "target-arrow-color": c("--text") } },
          { selector: "node.hl", style: { "background-color": c("--warn") } },
        ],
        layout: { name: "dagre", rankDir: "LR", nodeSep: 20, rankSep: 60 } as any,
        wheelSensitivity: 0.2,
      });
      cyRef.current.on("tap", "node", (ev) => setSel({ type: "state", fp: ev.target.id() }));
      cyRef.current.on("tap", "edge", (ev) => setSel({ type: "edge", key: ev.target.id() }));
      cyRef.current.on("tap", (ev) => {
        if (ev.target === cyRef.current) setSel(null);
      });
    } else {
      const cy = cyRef.current;
      cy.batch(() => {
        cy.elements().remove();
        cy.add(elements);
      });
      cy.layout({ name: "dagre", rankDir: "LR", nodeSep: 20, rankSep: 60 } as any).run();
    }
  }, [graph, pkgFilter]);

  useEffect(() => () => cyRef.current?.destroy(), []);

  const focus = (fp: string) => {
    setSel({ type: "state", fp });
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().unselect();
    const n = cy.getElementById(fp);
    if (n.nonempty()) {
      n.select();
      cy.animate({ center: { eles: n }, zoom: Math.max(cy.zoom(), 1.2) }, { duration: 250 });
    }
  };
  const focusEdge = (fromFp: string, action: string, toFp: string) => {
    const key = `${fromFp}|${action}|${toFp}`;
    setSel({ type: "edge", key });
    const cy = cyRef.current;
    if (!cy) return;
    cy.elements().unselect();
    const e = cy.getElementById(key);
    if (e.nonempty()) {
      e.select();
      cy.animate({ center: { eles: e } }, { duration: 250 });
    }
  };

  const selState = sel?.type === "state" ? byFp.get(sel.fp) : undefined;
  const selEdge = sel?.type === "edge" ? graph.edges.find((e) => `${e.fromFp}|${e.action}|${e.toFp}` === sel.key) : undefined;

  return (
    <div className="stack">
      <div className="toolbar">
        <h1 style={{ margin: 0 }}>State graph</h1>
        <span className="muted">
          {analysis.stateCount} states · {analysis.edgeCount} edges
        </span>
        <select value={pkgFilter} onChange={(e) => setPkgFilter(e.target.value)}>
          <option value="">all packages</option>
          {pkgs.map((p) => (
            <option key={p} value={p}>
              {p || "(none)"}
            </option>
          ))}
        </select>
        <label className="muted" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={liveOn} onChange={(e) => setLiveOn(e.target.checked)} /> live
        </label>
        <button className="small" onClick={() => cyRef.current?.fit(undefined, 30)}>
          fit
        </button>
        <button className="small" onClick={() => q.refetch()}>
          reload snapshot
        </button>
        <span style={{ marginLeft: "auto" }} className={`badge ${analysis.fragmentation ? "fail" : "ok"}`}>
          {analysis.fragmentation ? "⚠ likely fragmentation" : "fingerprints stable"}
        </span>
      </div>
      <div className="split">
        <div className="cy" ref={ref} />
        <div className="side stack">
          {selState && (
            <div className="panel">
              <h3>state</h3>
              <div className="fp">{selState.fp}</div>
              <dl className="kv">
                <dt>activity</dt>
                <dd>{selState.activity ?? "?"}</dd>
                <dt>pkg</dt>
                <dd className="mono">{selState.pkg}</dd>
                <dt>visits</dt>
                <dd>{selState.visits}</dd>
                <dt>first</dt>
                <dd>{fmtAgo(selState.firstTs)}</dd>
                <dt>last</dt>
                <dd>{fmtAgo(selState.lastTs)}</dd>
                <dt>contexts</dt>
                <dd className="mono">{(selState.contexts ?? []).join(", ") || "—"}</dd>
              </dl>
              <details>
                <summary className="muted">sampleIds ({(selState.sampleIds ?? []).length})</summary>
                <pre className="mono">{(selState.sampleIds ?? []).map(tail).join("\n")}</pre>
              </details>
              <h3 style={{ marginTop: 8 }}>out edges</h3>
              {graph.edges
                .filter((e) => e.fromFp === selState.fp)
                .map((e) => (
                  <div key={`${e.action}|${e.toFp}`} className="list-item" onClick={() => focusEdge(e.fromFp, e.action, e.toFp)}>
                    <span className="mono">{e.action}</span> → <span className="fp">{shortFp(e.toFp)}</span> {shortAct(byFp.get(e.toFp)?.activity)} <span className="muted">×{e.count}</span>
                  </div>
                ))}
            </div>
          )}
          {selEdge && (
            <div className="panel">
              <h3>edge</h3>
              <div className="mono">
                <span className="fp" onClick={() => focus(selEdge.fromFp)} style={{ cursor: "pointer" }}>{shortFp(selEdge.fromFp)}</span> --{selEdge.action}--&gt;{" "}
                <span className="fp" onClick={() => focus(selEdge.toFp)} style={{ cursor: "pointer" }}>{shortFp(selEdge.toFp)}</span>
              </div>
              <dl className="kv">
                <dt>count</dt>
                <dd>{selEdge.count}</dd>
                <dt>ok</dt>
                <dd>{selEdge.okCount}</dd>
                <dt>context</dt>
                <dd className="mono">{selEdge.context ?? "—"}</dd>
                <dt>first</dt>
                <dd>{fmtAgo(selEdge.firstTs)}</dd>
                <dt>last</dt>
                <dd>{fmtAgo(selEdge.lastTs)}</dd>
                <dt>commit</dt>
                <dd>{selEdge.action.includes("!") ? "yes (tap!)" : "no"}</dd>
              </dl>
            </div>
          )}
          <div className="panel">
            <h3>activities</h3>
            {analysis.unknownActivityVisits > 0 && <div className="muted" style={{ fontSize: 12 }}>{analysis.unknownActivityVisits} visits with unknown activity</div>}
            <Bars rows={analysis.activities.slice(0, 12).map((a) => ({ label: shortAct(a.activity), value: a.visits, title: `${a.pkg}/${a.activity} · ${a.states} states` }))} />
          </div>
          <div className="panel">
            <h3>edge instability</h3>
            {analysis.instability.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>none — every (state, action) landed on one fingerprint</div>
            ) : (
              analysis.instability.map((u) => (
                <div key={`${u.fromFp}|${u.action}`} className="list-item" onClick={() => focus(u.fromFp)}>
                  <span className="fp">{shortFp(u.fromFp)}</span> {shortAct(byFp.get(u.fromFp)?.activity)} <span className="mono">--{u.action}--&gt;</span> {u.landings.length} landings
                  <div className="muted" style={{ fontSize: 12 }}>
                    {u.landings.map((l) => (
                      <div key={l.toFp} onClick={(ev) => { ev.stopPropagation(); focusEdge(u.fromFp, u.action, l.toFp); }}>
                        ×{l.count} <span className="fp">{shortFp(l.toFp)}</span> {shortAct(byFp.get(l.toFp)?.activity)}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="panel">
            <h3>state aliasing (overlap ≥ 0.70)</h3>
            {analysis.aliasing.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>none — fingerprints separate cleanly</div>
            ) : (
              analysis.aliasing.map((p) => (
                <div key={`${p.a}|${p.b}`} className="list-item" onClick={() => focus(p.a)}>
                  <span className={`badge ${p.likelyFragmentation ? "fail" : "ok"}`}>{p.likelyFragmentation ? "likely fragmentation" : "distinct by behaviour"}</span>{" "}
                  <span className="muted">{shortAct(p.activity)}</span>
                  <div className="mono" style={{ fontSize: 12 }}>
                    <span className="fp">{shortFp(p.a)}</span> ~ <span className="fp" onClick={(ev) => { ev.stopPropagation(); focus(p.b); }}>{shortFp(p.b)}</span> overlap {p.overlap.toFixed(2)}
                  </div>
                  {p.onlyA.length > 0 && <div className="muted" style={{ fontSize: 12 }}>only {shortFp(p.a)}: {p.onlyA.join(", ")}</div>}
                  {p.onlyB.length > 0 && <div className="muted" style={{ fontSize: 12 }}>only {shortFp(p.b)}: {p.onlyB.join(", ")}</div>}
                  <div className="muted" style={{ fontSize: 12 }}>actions: [{p.actionsA.join(", ")}] vs [{p.actionsB.join(", ")}]</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
